import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useLanguage } from '../../context/LanguageContext';
import { i18n } from '../../i18n/i18n';
import { setRewardHandedOver } from '../../services/apiService';
import { rewardsToHandOver } from '../../utils/handOver';
import { KidButton, KidCard } from '../kid/KidUI';
import { StoredPicture } from '../kid/StoredPicture';
import { showToast } from '../kid/Toast';

/**
 * "Rewards to hand over" (Jens 2026-10-11): a child got a reward, so a grown-up should give it to
 * them. Shown on the parent's Home and in Admin → Rewards while anything is waiting; "Handed
 * over" takes it off the list (with Undo).
 */
const HandOverRewards: React.FC<{ className?: string }> = ({ className }) => {
  const { current_family, refreshFamilyData } = useAppContext() as any;
  const { current_language } = useLanguage();
  const [busy, setBusy] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const waiting = rewardsToHandOver(current_family?.rewards as any[]).filter((r: any) => !hidden.has(r.id));
  if (waiting.length === 0) return null;

  const nameOf = (id?: string) => current_family.members.find((m: any) => m.id === id)?.name || '';
  const whenText = (iso: any) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(current_language, { weekday: 'short', day: 'numeric', month: 'short' })
      + ' ' + d.toLocaleTimeString(current_language, { hour: '2-digit', minute: '2-digit' });
  };

  const markDone = async (reward: any) => {
    setBusy(reward.id);
    setHidden((h) => new Set(h).add(reward.id));
    try {
      await setRewardHandedOver(reward.id, true);
      await refreshFamilyData();
      showToast({
        message: i18n.t('handover.done_toast').replace('{{reward}}', reward.title).replace('{{name}}', nameOf(reward.claimed_by)),
        actionLabel: i18n.t('approve.undo'),
        onAction: async () => {
          try {
            await setRewardHandedOver(reward.id, false);
            setHidden((h) => { const n = new Set(h); n.delete(reward.id); return n; });
            await refreshFamilyData();
          } catch (e: any) {
            showToast({ message: e?.message || 'Error', tone: 'error' });
          }
        },
      });
    } catch (e: any) {
      setHidden((h) => { const n = new Set(h); n.delete(reward.id); return n; });
      showToast({ message: e?.message || 'Error', tone: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <KidCard tone="peach" className={`!border-2 !border-kid-orange ${className || ''}`}>
      <h2 className="font-kid-heading font-extrabold text-2xl md:text-3xl mb-1">
        🎁 {i18n.t('handover.title')}
      </h2>
      <p className="text-base mb-3">{i18n.t('handover.help')}</p>
      <ul className="space-y-3">
        {waiting.map((r: any) => (
          <li key={r.id} className="flex flex-wrap items-center gap-3 rounded-kid-card bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark p-3">
            <StoredPicture imageId={r.header_image} label={r.title} size={56} tint="peach" fit="cover" />
            <span className="flex-1 min-w-[10rem]">
              <span className="block font-bold text-lg leading-tight">
                {i18n.t('handover.got').replace('{{name}}', nameOf(r.claimed_by)).replace('{{reward}}', r.title)}
              </span>
              <span className="block text-sm">{whenText(r.claimed_at)}</span>
            </span>
            <KidButton size="md" disabled={busy === r.id} onClick={() => markDone(r)} icon={<span aria-hidden>✓</span>}>
              {i18n.t('handover.handed_over')}
            </KidButton>
          </li>
        ))}
      </ul>
    </KidCard>
  );
};

export default HandOverRewards;
